import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { Colour, Organisation } from 'src/app/global/global.model';
import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { OrganisationService } from 'src/app/global/services/organisation/organisation.abstract.service';
import { GetOrganisationColourAction, SetOrganisationColourAction } from 'src/app/global/store/global.actions';
import { languageSelector, organisationConfigurationSelector, organisationSelector } from 'src/app/global/store/global.selectors';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';
import { State } from 'src/app/store';

export const colorRegex = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

@Component({
    selector: 'app-config-menu-color',
    templateUrl: './config-menu-color.component.html',
    styleUrls: ['./config-menu-color.component.scss'],
})
export class ConfigMenuColorComponent implements OnInit {
    destroy$ = new Subject();

    loading = false;
    processingSubmit = false;
    org: Organisation;
    currLang: String;

    colourConfigForm = this.fb.group({
        background: new FormControl('', [Validators.required, Validators.pattern(colorRegex)]),
        icon: new FormControl('', [Validators.pattern(colorRegex)]),
        text: new FormControl({}, [Validators.pattern(colorRegex)]),
        name: new FormControl({}, [Validators.pattern(colorRegex)]),
        menu: new FormControl({}, [Validators.pattern(colorRegex)]),
    });

    public colourPayload: Colour;

    constructor(
        public translate: TranslateService,
        private configAllService: ConfigAllService,
        private store: Store<State>,
        private organisationService: OrganisationService,
        private messageService: MessageService,
        private fb: FormBuilder,
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.getTraductionTitle();
        this.store
            .select(languageSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translate.use(lang)),
            )
            .subscribe();

        this.store
            .select(organisationSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap(() => (this.loading = true)),
                filter((org) => !!org),
                tap((org) => {
                    this.org = org;

                    if (org.colour) {
                        // Convert the color strings to objects expected by the color picker
                        this.colourConfigForm.patchValue(
                            {
                                background: org.colour?.background,
                                icon: org.colour?.icon,
                                text: org.colour?.text,
                                name: org.colour?.name,
                                menu: org.colour?.menu,
                            },
                            { emitEvent: false },
                        );
                    } else {
                        this.colourConfigForm.patchValue(
                            {
                                background: null,
                                icon: null,
                                text: null,
                                name: null,
                                menu: null,
                            },
                            { emitEvent: false },
                        );
                    }

                    this.cdr.detectChanges();
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getTraductionTitle() {
        this.currLang = this.translate.currentLang;
    }

    refreshOrgConfig() {
        this.contextService.refreshOrganisationConfiguration().pipe(takeUntil(this.destroy$)).subscribe();
    }

    public cancel() {
        this.colourConfigForm.patchValue(
            {
                background: this.org.colour?.background,
                icon: this.org.colour?.icon,
                text: this.org.colour?.text,
                name: this.org.colour?.name,
                menu: this.org.colour?.menu,
            },
            { emitEvent: false },
        );

        this.messageService.success(this.translate.instant('pages.config-all.config-requests.messages.cancelled'));
    }

    public setDefault() {
        this.colourConfigForm.patchValue(
            {
                background: '#ffffff',
                icon: '#8792a5',
                text: '#57C2F0',
                name: '#000000',
                menu: '#526483',
            },
            { emitEvent: false },
        );
    }

    public submit() {
        this.processingSubmit = true;

        this.colourPayload = {
            background: this.getHexValue('background'),
            icon: this.getHexValue('icon'),
            text: this.getHexValue('text'),
            name: this.getHexValue('name'),
            menu: this.getHexValue('menu'),
        };

        this.store.dispatch(
            SetOrganisationColourAction({
                orgTag: this.org?.tag,
                colour: this.colourPayload,
            }),
        );

        this.processingSubmit = false;
        this.messageService.success(this.translate.instant('pages.config-all.config-requests.messages.success'));
    }

    private getHexValue(control: string): string {
        const value = this.colourConfigForm.get(control)?.value;
        if (!value) return '#ffffff';
        return value.hex ? `#${value.hex}` : value;
    }

    /**
     * Validate the form.
     */
    public validateForm() {
        return this.colourConfigForm.valid;
    }

    /**
     * GETTERS
     */
    get proofOfIdentityCount() {
        return this.colourConfigForm.get('background');
    }

    getToolbarStyle(): { [key: string]: string } {
        if (this.getHexValue('background') == '#ffffff') {
            return {
                backgroundColor: '#ffffff',
            };
        }
        return {
            backgroundColor: this.getHexValue('background'),
        };
    }

    getIconStyle(): { [key: string]: string } {
        if (this.getHexValue('icon') == '#ffffff') {
            return {
                color: '#8792a5',
            };
        }
        return {
            color: this.getHexValue('icon'),
        };
    }

    getTextStyle(): { [key: string]: string } {
        if (this.getHexValue('text') == '#ffffff') {
            return {
                color: '#57C2F0',
            };
        }
        return {
            color: this.getHexValue('text'),
        };
    }

    getNameStyle(): { [key: string]: string } {
        if (this.getHexValue('name') == '#ffffff') {
            return {
                color: '#000000',
            };
        }
        return {
            color: this.getHexValue('name'),
        };
    }

    getMenuStyle(): { [key: string]: string } {
        if (this.getHexValue('menu') == '#ffffff') {
            return {
                color: '#526483',
            };
        }
        return {
            color: this.getHexValue('menu'),
        };
    }
}
